<Banner
    closable={bannerSettings.isCloseable}
    close={bannerSettings.closeButtonName ?? i18n.close}
    html={bannerSettings.message}
    type={bannerSettings.feedbackType}
    visible={bannerSettings.isVisible}
    on:hidden={onBannerHidden}
/>

<script>
    import Banner from "@itslearning/prometheus/assets/feedback/Banner/v1/Banner.svelte";

    import {
        onMount,
    } from "svelte";
    import {
        checkCopyCourseStatus,
        dismissCourseCopyErrors,
    } from "../api";

    /**
     * @typedef {object} CopyCourseStatusCheckConfigModel
     * @property {number} courseId
     * @property {number} locationType
     * @property {number} userId
     * @property {boolean} isCourseInCopyState
     * @property {boolean} shouldTriggerAutomaticCopyCourse
     * @property {boolean} isTeacherInCourse
     * @property {number} checkIfCopyingCourseIsInProgressInMs
     * @property {string} getCourseCopyStatusEndpoint
     * @property {string} showContentAreaSimpleFeedbackPostMessage
     * @property {string} courseCopyStatusNotInProgress
     * @property {string} courseCopyStatusInProgress
     * @property {string} courseCopyStatusLearningToolElementsInProgress
     * @property {string} courseCopyStatusPlannerInProgress
     * @property {string} courseCopyStatusPlannerFailed
     * @property {string} courseCopyStatusPartlyFailed
     * @property {string} courseCopyStatusFailed
     * @property {string} contentAreaIframeClientId
     * @property {boolean} isCourseInProgressOrFailed
     * @property {string} courseCopyCompleteMessage
     */

    /** @type {string} */
    export let antiForgeryHeaderName;

    /** @type {string} */
    export let antiForgeryHeaderValue;

    /** @type {CopyCourseStatusCheckConfigModel} */
    export let copyCourseStatusCheckConfigModel;

    /** @type {{close: string;}} */
    export let i18n;

    /** @type {import('../api').FeedbackBannerModel} */
    let bannerSettings = {
        closeActionType: null,
        closeButtonName: null,
        data: null,
        feedbackType: "warning",
        isCloseable: true,
        isVisible: false,
        message: null,
    };

    let copyCourseStatusCheckIntervalId = null;
    let wasCopyInProgress = false;

    function getCopyJustCompletedStorageKey() {
        return `courseCopyJustCompleted:${copyCourseStatusCheckConfigModel.courseId}`;
    }

    onMount(async () => {
        if (!copyCourseStatusCheckConfigModel) {
            return;
        }

        // If we reloaded the page right after the copy finished (so the newly copied
        // plans/topics are rendered), show the completion banner and skip the status check.
        if (sessionStorage.getItem(getCopyJustCompletedStorageKey())) {
            sessionStorage.removeItem(getCopyJustCompletedStorageKey());
            showCourseCopyCompleteBanner();
            return;
        }

        checkStatus();
    });

    function checkStatus() {
        if (copyCourseStatusCheckConfigModel
            && copyCourseStatusCheckConfigModel.isTeacherInCourse
            && copyCourseStatusCheckConfigModel.isCourseInProgressOrFailed
        ) {
            // Perform an initial check on the status of the course, so display any feedback messages
            runCopyCourseStatusCheck();

            // Poll for the status of the course if needed
            if (copyCourseStatusCheckConfigModel.isCourseInCopyState || copyCourseStatusCheckConfigModel.shouldTriggerAutomaticCopyCourse) {
                clearCourseStatusCheckerTimer();

                copyCourseStatusCheckIntervalId = setInterval(
                    () => runCopyCourseStatusCheck(),
                    copyCourseStatusCheckConfigModel.checkIfCopyingCourseIsInProgressInMs
                );
            }
        }
    }

    async function runCopyCourseStatusCheck() {
        try {
            const response = await checkCopyCourseStatus(copyCourseStatusCheckConfigModel.courseId);

            switch (response.copyStatus) {
                case copyCourseStatusCheckConfigModel.courseCopyStatusNotInProgress:
                    clearCourseStatusCheckerTimer();
                    if (wasCopyInProgress) {
                        // Copy just finished during this session. Reload the page so the newly
                        // copied plans/topics are rendered, then show the info banner afterwards.
                        wasCopyInProgress = false;
                        sessionStorage.setItem(getCopyJustCompletedStorageKey(), "1");
                        window.location.reload();
                    } else {
                        updateBannerSettings({ isVisible: false });
                    }
                    break;
                case copyCourseStatusCheckConfigModel.courseCopyStatusInProgress:
                case copyCourseStatusCheckConfigModel.courseCopyStatusPlannerInProgress:
                case copyCourseStatusCheckConfigModel.courseCopyStatusLearningToolElementsInProgress:
                    wasCopyInProgress = true;
                    updateBannerSettings(response.feedbackBanner);
                    break;
                case copyCourseStatusCheckConfigModel.courseCopyStatusFailed:
                case copyCourseStatusCheckConfigModel.courseCopyStatusPlannerFailed:
                case copyCourseStatusCheckConfigModel.courseCopyStatusPartlyFailed:
                    clearCourseStatusCheckerTimer();
                    updateBannerSettings(response.feedbackBanner);
                    break;
            }
        } catch (error) {
            console.error('Error checking copy course status: ', error);
        }
    }

    function clearCourseStatusCheckerTimer() {
        if (copyCourseStatusCheckIntervalId) {
            clearInterval(copyCourseStatusCheckIntervalId);
        }
    }

    /**
     * @param {import('../api').FeedbackBannerModel} feedbackBanner
     */
    function updateBannerSettings(feedbackBanner) {
        bannerSettings = {
            ...bannerSettings,
            ...feedbackBanner,
        };
    }

    function showCourseCopyCompleteBanner() {
        updateBannerSettings({
            feedbackType: "info",
            isCloseable: true,
            isVisible: true,
            message: copyCourseStatusCheckConfigModel.courseCopyCompleteMessage,
            closeActionType: null,
            data: null,
        });
    }

    function onBannerHidden() {
        bannerSettings.isVisible = false;

        if (bannerSettings?.closeActionType === "dismissCopyCourse") {
            dismissCourseCopyErrors(
                bannerSettings.data.courseId,
                antiForgeryHeaderName,
                antiForgeryHeaderValue
            );
        }
    }
</script>
